/**
 * @file HeldHerePanel.js
 * @version 1.0.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-11-03
 * @modified 2026-11-03
 * @description Approved here, not yet in Xero — the invoice window's block
 *              (Cam, 3 Nov 2026: "Need to be able to set invoices as approved/
 *              authorised local-only ready to send on statements even when
 *              stock issues or whatever block this going to Xero. When this is
 *              done there needs to be a persistant alert raised to rectify it
 *              later.").
 *
 *   Held (invoice.heldHere): who held it, when, Xero's reason, the last try,
 *   a locked date — and "Send to Xero now" (POST /api/invoices/:id/send-held).
 *   It is on statements and goes to Xero by itself once Xero takes it; the
 *   persistent alert lists it until then.
 *
 *   Not held, made here, approved, and Xero refused it on its way
 *   (invoice.xeroPushError, holdBlock null): "Keep held here"
 *   (POST /api/invoices/:id/hold-here) moves it from the ordinary "not in
 *   Xero" alert to the persistent one, with its reason.
 *
 *   Nothing otherwise. Greyed with the reason when the person cannot.
 */

import React, { useState } from 'react';
import { useSlideOutButton } from './SlideOutButton';
import { holdHere, sendHeldNow } from '../utils/heldHere';
import './HeldHerePanel.css';

/** What the panel shows for this invoice: 'held' | 'offer' | null. Pure. */
export function heldPanelMode(invoice) {
  if (!invoice) return null;
  if (invoice.heldHere) return 'held';
  if (invoice.xeroPushError && !invoice.holdBlock && !invoice.xeroInvoiceId) return 'offer';
  return null;
}

/**
 * @param {object}   p.invoice     GET /api/invoices/:id's invoice
 * @param {boolean}  p.canHold     Create invoices, not view only
 * @param {boolean}  p.canSend     Create or Edit invoices, not view only
 * @param {string}   [p.whyNot]    why the person cannot (view only)
 * @param {function} p.onChanged   re-read the invoice
 * @param {function} p.notify      (message, type) — the window's alert
 */
export default function HeldHerePanel({ invoice, canHold = false, canSend = false, whyNot = null, onChanged, notify }) {
  const [working, setWorking] = useState(false);
  const mode = heldPanelMode(invoice);
  useSlideOutButton([mode, working]);
  if (!mode) return null;

  const no = invoice.invoiceNo || 'This invoice';
  const run = async (fn, ok) => {
    setWorking(true);
    try {
      const body = await fn(invoice.id);
      if (notify) notify(body?.message || ok, body?.outcome && body.outcome !== 'sent' ? 'warning' : 'success');
    } catch (err) {
      if (notify) notify(err.message, 'error');
    } finally {
      setWorking(false);
      if (onChanged) await onChanged();
    }
  };

  if (mode === 'held') {
    const h = invoice.heldHere;
    const block = !canSend ? (whyNot || "Sending it needs 'Create invoices' or 'Edit invoices'") : (working ? 'Sending…' : null);
    return (
      <div className="held-here-panel is-held" role="status">
        <div className="held-here-head">
          <span className="material-symbols-rounded" aria-hidden="true">cloud_sync</span>
          <strong>Approved here, not yet in Xero</strong>
          <span className="held-here-pill">{h.in_xero ? 'A draft in Xero' : 'Not in Xero'}</span>
        </div>
        <p>{h.text}</p>
        {h.last_error && <p className="held-here-meta">Last try: {h.last_error}</p>}
        {h.locked && <p className="held-here-locked">{h.locked}</p>}
        <div className="buttons slideout held-here-actions">
          <div>
            <button type="button" data-write-action
              className={`btn-slideout${block ? ' btn-disabled' : ''}`}
              disabled={!!block}
              title={block || `Send ${no} to Xero now — it goes if Xero takes it (the stock put right, Xero answering)`}
              onClick={() => run(sendHeldNow, `${no} sent to Xero.`)}>
              <div className="icon-wrapper"><span className="material-symbols-rounded">cloud_upload</span></div>
              <span className="btn-label">{working ? 'Sending…' : 'Send to Xero now'}</span>
            </button>
          </div>
        </div>
      </div>
    );
  }

  const block = !canHold ? (whyNot || "Holding it needs 'Create invoices'") : (working ? 'Saving…' : null);
  return (
    <div className="held-here-panel is-offer" role="status">
      <div className="held-here-head">
        <span className="material-symbols-rounded" aria-hidden="true">cloud_off</span>
        <strong>Not in Xero — Xero refused it</strong>
      </div>
      <p>{invoice.xeroPushError}</p>
      <p className="held-here-meta">
        It is approved here and tried again automatically. Keep it held here to say so on purpose: it stays on statements,
        goes to Xero once Xero takes it, and an alert stays for everyone until it is in Xero.
      </p>
      <div className="buttons slideout held-here-actions">
        <div>
          <button type="button" data-write-action
            className={`btn-slideout${block ? ' btn-disabled' : ''}`}
            disabled={!!block}
            title={block || `Keep ${no} held here — approved here only, sent to Xero once fixed`}
            onClick={() => run(holdHere, `${no} is held here.`)}>
            <div className="icon-wrapper"><span className="material-symbols-rounded">push_pin</span></div>
            <span className="btn-label">{working ? 'Saving…' : 'Keep held here'}</span>
          </button>
        </div>
      </div>
    </div>
  );
}
